import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Plus, Trash2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DENTAL_PULSE_LOGO = 'https://customer-assets.emergentagent.com/job_dentalpulse-4/artifacts/3espo6gq_DentalPulse_logoFinal%20%2B%20white.png';

export default function SetupPage() {
  const [practiceName, setPracticeName] = useState('');
  const [operatoryNames, setOperatoryNames] = useState(['Operatory 1', 'Operatory 2', 'Operatory 3']);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const addOperatory = () => {
    setOperatoryNames([...operatoryNames, `Operatory ${operatoryNames.length + 1}`]);
  };

  const removeOperatory = (index) => {
    if (operatoryNames.length > 1) {
      setOperatoryNames(operatoryNames.filter((_, i) => i !== index));
    }
  };

  const updateOperatory = (index, value) => {
    const updated = [...operatoryNames];
    updated[index] = value;
    setOperatoryNames(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/practice/setup`, {
        name: practiceName,
        operatory_count: operatoryNames.length,
        operatory_names: operatoryNames
      });
      
      toast.success('Practice setup complete!');
      navigate('/login');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Setup failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <div className="w-full max-w-2xl">
        <div className="glass-panel rounded-3xl p-8 shadow-2xl">
          <div className="text-center mb-8">
            <img 
              src={DENTAL_PULSE_LOGO} 
              alt="DentalPulse"
              className="h-16 w-auto mx-auto mb-4"
              data-testid="setup-logo"
            />
            <h1 className="text-4xl font-heading font-bold text-slate-800 mb-2">Setup Your Practice</h1>
            <p className="text-slate-600">Configure your dental office</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="practiceName" className="text-slate-700 font-medium">Practice Name</Label>
              <Input
                id="practiceName"
                data-testid="practice-name-input"
                type="text"
                value={practiceName}
                onChange={(e) => setPracticeName(e.target.value)}
                className="mt-2 bg-white/50 border-white/30"
                placeholder="e.g., Bright Smiles Dental"
                required
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-3">
                <Label className="text-slate-700 font-medium">Operatories</Label>
                <Button
                  type="button"
                  onClick={addOperatory}
                  data-testid="add-operatory-button"
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" /> Add
                </Button>
              </div>

              <div className="space-y-2">
                {operatoryNames.map((name, index) => (
                  <div key={index} className="flex gap-2">
                    <Input
                      value={name}
                      onChange={(e) => updateOperatory(index, e.target.value)}
                      className="bg-white/50 border-white/30"
                      data-testid={`operatory-input-${index}`}
                    />
                    {operatoryNames.length > 1 && (
                      <Button
                        type="button"
                        onClick={() => removeOperatory(index)}
                        variant="outline"
                        size="icon"
                        data-testid={`remove-operatory-${index}`}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <Button
              type="submit"
              data-testid="setup-submit-button"
              disabled={loading}
              className="w-full bg-dp-600 hover:bg-dp-700 text-white font-semibold py-6 rounded-xl"
            >
              {loading ? 'Setting up...' : 'Complete Setup'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}